import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldStar, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login, demoLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  // One-click sign-in. The SERVER decides whether this list has anything in it:
  // with the deployment flag off, or the buttons hidden by an administrator, or
  // the demo accounts purged, it comes back empty and nothing renders.
  const [demoAccounts, setDemoAccounts] = useState([]);
  const [demoBusy, setDemoBusy] = useState("");

  useEffect(() => {
    api.get("/auth/demo-accounts")
      .then((r) => setDemoAccounts(r.data?.enabled ? (r.data.accounts || []) : []))
      .catch(() => setDemoAccounts([]));
  }, []);

  const land = (user) => {
    toast.success(`Welcome, ${user.name}`);
    navigate(user.role === "guard" ? "/m" : "/", { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      land(await login(email, password));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const quick = async (addr) => {
    setDemoBusy(addr);
    try {
      land(await demoLogin(addr));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setDemoBusy("");
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background" data-testid="login-page">
      <div className="flex items-center px-8 md:px-16 py-12">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 bg-foreground text-background grid place-items-center">
              <ShieldStar size={22} weight="fill" />
            </div>
            <div>
              <div className="font-heading font-black text-xl tracking-tight leading-none">AEGIS<span className="text-[hsl(var(--warning))]">OPS</span></div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">Guard Deployment & Operations</div>
            </div>
          </div>
          <h1 className="font-heading text-3xl font-black tracking-tight mb-2">Sign in</h1>
          <p className="text-sm text-muted-foreground mb-8">Office console and field operations, one account.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.1em] font-bold">Email</Label>
              <Input id="email" data-testid="login-email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} className="rounded-none mt-1 h-11" autoComplete="email" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.1em] font-bold">Password</Label>
              <Input id="password" data-testid="login-password" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)} className="rounded-none mt-1 h-11" autoComplete="current-password" />
            </div>
            <Button data-testid="login-submit" type="submit" disabled={busy}
              className="w-full rounded-none h-11 font-heading font-bold tracking-wide">
              {busy ? "SIGNING IN…" : "SIGN IN"}
            </Button>
          </form>

          {demoAccounts.length > 0 && (
            <div className="mt-10" data-testid="demo-logins">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1 bg-border" />
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground whitespace-nowrap">
                  Or sign in as
                </div>
                <div className="h-px flex-1 bg-border" />
              </div>
              <div className="border border-border divide-y divide-border">
                {demoAccounts.map((a) => (
                  <button
                    key={a.email}
                    type="button"
                    data-testid={`demo-login-${a.role}`}
                    disabled={!!demoBusy}
                    onClick={() => quick(a.email)}
                    className="w-full flex items-center gap-3 px-3 h-11 text-left transition-colors hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="font-heading text-xs font-bold tracking-wide uppercase flex-1 truncate">
                      {a.role_label}
                    </span>
                    <span className="font-mono text-[10px] text-muted-foreground truncate max-w-[9rem]">
                      {demoBusy === a.email ? "SIGNING IN…" : a.name}
                    </span>
                    <CaretRight size={12} weight="bold" className="text-muted-foreground shrink-0" />
                  </button>
                ))}
              </div>
              <p className="font-mono text-[10px] text-muted-foreground mt-2 leading-relaxed">
                Demonstration accounts. No password is shown or sent — the server signs you in.
              </p>
            </div>
          )}

          <p className="font-mono text-[10px] text-muted-foreground mt-8 leading-relaxed">
            Accounts are provisioned by your system administrator. 5 failed attempts locks sign-in for 15 minutes.
          </p>
        </div>
      </div>
      <div className="hidden md:block relative">
        <img
          src="https://images.unsplash.com/photo-1621831337128-35676ca30868?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBidWlsZGluZ3xlbnwwfHx8fDE3ODc0NjE0NTZ8MA&ixlib=rb-4.1.0&q=85"
          alt="Secured facility"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] mb-3 text-[#FFD60A]">Asia/Manila · PHP · Multi-branch</div>
          <div className="font-heading text-4xl font-black tracking-tighter leading-tight">
            Every post covered.<br />Every hour accounted.
          </div>
        </div>
      </div>
    </div>
  );
}
